import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Stack,
  Alert,
  TextField,
  IconButton,
  Divider,
  Chip,
  Button,
  Grid,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import TableRestaurantRoundedIcon from "@mui/icons-material/TableRestaurantRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "src/redux/store";
import { GetZonas, bulkSaveMesas, getMesas } from "src/redux/slices/mesas";
import { mesaNumber, nextMesaNumber } from "./mesaNumber";

// ----------------------------------------------------------------------
//   Step 2 · Mesas · layout 2 columnas (2026-08-13):
//     · Izq: contadores +/- por zona (rápido, compacto).
//     · Der: lista editable con click-to-edit inline.
//   Sin scroll largo. Sin formulario aparte para añadir — se crean con
//   el incrementador y luego se editan detalles individuales en la
//   lista si hace falta. En mobile stack vertical (contadores arriba,
//   lista abajo).
// ----------------------------------------------------------------------

export default function StepMesas({ onComplete }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const zonas = useSelector((s) => s.mesas.zonas || []);
  const mesas = useSelector((s) => s.mesas.mesas || []);
  const [busy, setBusy] = useState(false);
  // Estado de edición inline (una mesa a la vez).
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ nombre: "", capacidad: 4, id_mesa: 0 });

  useEffect(() => {
    dispatch(GetZonas());
    dispatch(getMesas());
  }, [dispatch]);

  const zonasById = useMemo(() => {
    const m = {};
    zonas.forEach((z) => { m[z.id] = z; });
    return m;
  }, [zonas]);

  const mesasByZona = useMemo(() => {
    const groups = {};
    (mesas || []).forEach((m) => {
      if (!groups[m.zonaId]) groups[m.zonaId] = [];
      groups[m.zonaId].push(m);
    });
    Object.values(groups).forEach((arr) =>
      arr.sort((a, b) => mesaNumber(a) - mesaNumber(b)),
    );
    return groups;
  }, [mesas]);

  const nextNumForZona = (zonaId) => {
    return nextMesaNumber(mesasByZona[zonaId]);
  };

  const countZona = (zid) => (mesasByZona[zid] || []).length;
  const totalMesas = (mesas || []).length;

  // ─── Contadores +/- ─────────────────────────────────────────────────
  const inc = async (zid) => {
    if (busy) return;
    setBusy(true);
    try {
      await dispatch(bulkSaveMesas([{
        id: null,
        id_mesa: nextNumForZona(zid),
        nombre: null,
        capacidad: 4,
        zonaId: zid,
        available: true,
        deleted: false,
      }]));
      await dispatch(getMesas({ fresh: true }));
    } catch (err) {
      enqueueSnackbar(err?.message || "Error añadiendo mesa", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const dec = async (zid) => {
    if (busy) return;
    const arr = mesasByZona[zid] || [];
    if (arr.length === 0) return;
    // Elimina la de mayor número (la más reciente por convención).
    const last = arr[arr.length - 1];
    setBusy(true);
    try {
      await dispatch(bulkSaveMesas([{
        id: last.id,
        id_mesa: mesaNumber(last),
        nombre: last.nombre || null,
        capacidad: Number(last.capacidad) || 4,
        zonaId: last.zonaId,
        available: !!last.available,
        deleted: true,
      }]));
      await dispatch(getMesas({ fresh: true }));
      if (editingId === last.id) setEditingId(null);
    } catch (err) {
      enqueueSnackbar(err?.message || "Error eliminando mesa", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  // ─── Edición inline ────────────────────────────────────────────────
  const startEdit = (m) => {
    setEditingId(m.id);
    setEditDraft({
      nombre: m.nombre || "",
      capacidad: Number(m.capacidad) || 4,
      id_mesa: mesaNumber(m),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ nombre: "", capacidad: 4, id_mesa: 0 });
  };

  const saveEdit = async (m) => {
    if (busy) return;
    const numero = Number(editDraft.id_mesa) || 0;
    if (numero <= 0) {
      enqueueSnackbar("El número debe ser > 0", { variant: "warning" });
      return;
    }
    // Guard duplicado en la misma zona (excluyendo la mesa que se edita).
    const zonaMesas = mesasByZona[m.zonaId] || [];
    const conflict = zonaMesas.find(
      (x) => mesaNumber(x) === numero && x.id !== m.id,
    );
    if (conflict) {
      enqueueSnackbar(`Ya existe mesa ${numero} en esta zona`, { variant: "warning" });
      return;
    }
    setBusy(true);
    try {
      await dispatch(bulkSaveMesas([{
        id: m.id,
        id_mesa: numero,
        nombre: editDraft.nombre?.trim() || null,
        capacidad: Number(editDraft.capacidad) || 4,
        zonaId: m.zonaId,
        available: !!m.available,
        deleted: false,
      }]));
      await dispatch(getMesas({ fresh: true }));
      cancelEdit();
    } catch (err) {
      enqueueSnackbar(err?.message || "Error guardando cambios", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (m) => {
    if (busy) return;
    setBusy(true);
    try {
      await dispatch(bulkSaveMesas([{
        id: m.id,
        id_mesa: mesaNumber(m),
        nombre: m.nombre || null,
        capacidad: Number(m.capacidad) || 4,
        zonaId: m.zonaId,
        available: !!m.available,
        deleted: true,
      }]));
      await dispatch(getMesas());
      if (editingId === m.id) cancelEdit();
    } catch (err) {
      enqueueSnackbar(err?.message || "Error eliminando", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = async () => {
    if (totalMesas === 0) {
      enqueueSnackbar("Añade al menos 1 mesa para continuar", { variant: "warning" });
      return;
    }
    await onComplete({ dataJson: JSON.stringify({ totalMesas }) });
  };

  if (!zonas.length) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography sx={{ color: "text.secondary" }}>
          Vuelve al paso anterior y añade al menos una zona.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Mesas de tu local
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 2 }}>
        Ajusta cantidad con <b>+/-</b> por zona; toca cualquier mesa a la
        derecha para editarle nombre, número o capacidad.
      </Typography>

      {totalMesas > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {totalMesas} mesa{totalMesas === 1 ? "" : "s"} configurada{totalMesas === 1 ? "" : "s"}.
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Columna izquierda — contadores +/- por zona (compacto, sin scroll) */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary", display: "block", mb: 1 }}>
              Cantidad por zona
            </Typography>
            <Stack spacing={1.5}>
              {zonas.map((z) => {
                const n = countZona(z.id);
                return (
                  <Stack
                    key={z.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ py: 0.5 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        {z.nombre}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <IconButton
                        size="small"
                        aria-label={`Quitar una mesa de ${z.nombre}`}
                        onClick={() => dec(z.id)}
                        disabled={busy || n === 0}
                        sx={{ bgcolor: "grey.100", "&:hover": { bgcolor: "grey.200" } }}
                      >
                        <RemoveRoundedIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 900, minWidth: 40, textAlign: "center", fontVariant: "tabular-nums" }}
                      >
                        {n}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label={`Añadir una mesa a ${z.nombre}`}
                        onClick={() => inc(z.id)}
                        disabled={busy}
                        sx={{ bgcolor: "primary.lighter", color: "primary.main", "&:hover": { bgcolor: "primary.light" } }}
                      >
                        <AddRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Card>
        </Grid>

        {/* Columna derecha — lista editable con click-to-edit */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ p: 2, maxHeight: 480, overflow: "auto" }}>
            <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary", display: "block", mb: 1 }}>
              Detalle · edita nombre/número/capacidad
            </Typography>
            {totalMesas === 0 && (
              <Typography variant="body2" sx={{ color: "text.disabled", py: 3, textAlign: "center" }}>
                Sin mesas todavía. Usa los <b>+</b> de la izquierda para añadir.
              </Typography>
            )}
            {zonas.map((zona) => {
              const list = mesasByZona[zona.id] || [];
              if (list.length === 0) return null;
              return (
                <Box key={zona.id} sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={800} sx={{ color: "text.secondary", textTransform: "uppercase" }}>
                      {zona.nombre}
                    </Typography>
                    <Chip size="small" label={list.length} sx={{ height: 18, fontWeight: 700, fontSize: 10 }} />
                  </Stack>
                  <Stack spacing={0.75}>
                    {list.map((m) => {
                      const isEditingThis = editingId === m.id;
                      if (isEditingThis) {
                        return (
                          <Card
                            key={m.id}
                            variant="outlined"
                            sx={{ p: 1.25, borderColor: "primary.main", borderWidth: 2, bgcolor: "primary.lighter" }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                size="small"
                                label="Nº"
                                type="number"
                                value={editDraft.id_mesa || ""}
                                onChange={(e) => setEditDraft((p) => ({ ...p, id_mesa: Number(e.target.value) || 0 }))}
                                inputProps={{ min: 1 }}
                                sx={{ width: 70 }}
                              />
                              <TextField
                                size="small"
                                label="Nombre"
                                value={editDraft.nombre}
                                onChange={(e) => setEditDraft((p) => ({ ...p, nombre: e.target.value }))}
                                placeholder="(opcional)"
                                sx={{ flex: 1, minWidth: 0 }}
                              />
                              <TextField
                                size="small"
                                label="Cap."
                                type="number"
                                value={editDraft.capacidad || ""}
                                onChange={(e) => setEditDraft((p) => ({ ...p, capacidad: Number(e.target.value) || 0 }))}
                                inputProps={{ min: 1 }}
                                sx={{ width: 70 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => saveEdit(m)}
                                disabled={busy}
                                sx={{ color: "success.main" }}
                                aria-label="Guardar"
                              >
                                <SaveRoundedIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={cancelEdit}
                                disabled={busy}
                                aria-label="Cancelar"
                              >
                                <CloseRoundedIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Card>
                        );
                      }
                      return (
                        <Card
                          key={m.id}
                          variant="outlined"
                          onClick={() => startEdit(m)}
                          sx={{
                            p: 1.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            cursor: "pointer",
                            transition: "background-color 0.15s",
                            "&:hover": { backgroundColor: "primary.lighter" },
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                            <TableRestaurantRoundedIcon color="action" fontSize="small" />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={700} noWrap>
                                Mesa {mesaNumber(m)}{m.nombre ? ` · ${m.nombre}` : ""}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {m.capacidad || 4} comensales
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={0.25}>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); startEdit(m); }}
                              aria-label="Editar"
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); removeOne(m); }}
                              aria-label="Eliminar"
                              sx={{ color: "error.main" }}
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Card>
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={handleContinue}
          disabled={busy || totalMesas === 0}
        >
          Continuar →
        </Button>
      </Stack>
    </Box>
  );
}
